import { useState, useEffect } from "react";
import { Box, Flex, Link, Text } from "rebass/styled-components";
import { useForm, SubmitHandler, FieldValues } from "react-hook-form";
import styled from "styled-components";
import { useMutation } from "@apollo/client";

import { SettingsContainer } from "../../../../components/molecules/settingsContainer";
import { LabeledInputText, Error, Success } from "MVVM/Components/Inputs";
import { UserIcon, AlertIcon } from "../../../../components/atoms/icons";
import { validateUsername } from "MVVM/Utilities/User";
import { useHasProAccess } from "MVVM/Hooks/useHasProAccess";
import { useShouldShowSubscriptionOverhaul } from "MVVM/Hooks/useSubscriptionOverhaul";
import { USER_UPDATE_USERNAME } from "MVVM/GraphQL/userUpdateUsername";
import { Button } from "MVVM/Components/Button";
import { Divider } from "MVVM/Components/Divider";
import { analytics } from "MVVM/Analytics";

interface ITimeData {
  weeks: number;
  days: number;
  totalDays?: number;
}

interface IAllstarUser {
  user: {
    profile: {
      username: string;
    };
    usernameLastChanged: string | null;
  };
}

interface IUsernameChangeProps {
  allstarUser: IAllstarUser;
  getFreshProfile: () => Promise<void>;
}

const UsernameChange = ({
  allstarUser,
  getFreshProfile,
}: IUsernameChangeProps) => {
  const [, setDone] = useState(false);
  const [lastAttemptedUsername, setLastAttemptedUsername] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const {
    handleSubmit,
    register,
    formState,
    formState: { errors },
  } = useForm();

  const [state, setState] = useState({
    error: false,
    success: false,
    errorMsg: "",
    successMsg: "Username updated successfully!",
  });

  const hasProAccess = useHasProAccess();
  const showOverhaul = useShouldShowSubscriptionOverhaul();
  const currentUsername = allstarUser?.user?.profile?.username;

  const canChangeUsername = () => {
    if (!hasProAccess) return false;

    const lastChanged = allstarUser?.user?.usernameLastChanged;
    if (!lastChanged) return true;

    const oneMonthAgo = new Date();
    oneMonthAgo.setMonth(oneMonthAgo.getMonth() - 1);

    return new Date(lastChanged) < oneMonthAgo;
  };

  const getTimeRemaining = () => {
    const lastChanged = allstarUser?.user?.usernameLastChanged;

    if (!lastChanged) return null;

    const oneMonthFromChange = new Date(lastChanged);
    oneMonthFromChange.setMonth(oneMonthFromChange.getMonth() + 1);

    const now = new Date();
    const timeDiff = oneMonthFromChange.getTime() - now.getTime();

    if (timeDiff <= 0) return null;

    const days = Math.ceil(timeDiff / (1000 * 60 * 60 * 24));
    const weeks = Math.floor(days / 7);

    if (weeks > 0) return { weeks, days: days % 7, totalDays: days };

    return { weeks: 0, days, totalDays: days };
  };

  const formatTimeRemaining = (timeData: ITimeData | null) => {
    if (!timeData) return "";

    if (timeData.weeks > 0) {
      const weekText = timeData.weeks === 1 ? "week" : "weeks";
      if (timeData.days > 0) {
        const dayText = timeData.days === 1 ? "day" : "days";
        return `${timeData.weeks} ${weekText} and ${timeData.days} ${dayText}`;
      }
      return `${timeData.weeks} ${weekText}`;
    }

    const dayText = timeData.days === 1 ? "day" : "days";
    return `${timeData.days} ${dayText}`;
  };

  const [updateUsername] = useMutation(USER_UPDATE_USERNAME, {
    onError: (err) => {
      const errorMessage = err.graphQLErrors?.[0]?.message || err.message;
      const formattedError = formatErrorMessage(
        errorMessage,
        lastAttemptedUsername,
      );
      setState({ ...state, error: true, errorMsg: formattedError });
      setIsSubmitting(false);

      analytics.track("Username Change Failed", {
        error: errorMessage,
        attemptedUsername: lastAttemptedUsername,
      });
    },
    onCompleted: async (data) => {
      if (data.userUpdateUsername.errors?.length > 0) {
        const errorMessage = data.userUpdateUsername.errors[0].message;
        const formattedError = formatErrorMessage(
          errorMessage,
          lastAttemptedUsername,
        );
        setState({ ...state, error: true, errorMsg: formattedError });
        setIsSubmitting(false);
      } else {
        await getFreshProfile();
        setDone(true);
        setState({ ...state, success: true, errorMsg: "" });
        setIsSubmitting(false);

        analytics.track("Username Changed Successfully", {
          newUsername: lastAttemptedUsername,
          previousUsername: currentUsername,
        });
      }
    },
  });

  useEffect(() => {
    if (!state.success && !state.error) return;
    setTimeout(() => {
      return setState((state) => {
        return {
          ...state,
          success: false,
          error: false,
          errorMsg: "",
        };
      });
    }, 5000);
  }, [state.success, state.error]);

  const formatErrorMessage = (
    errorMessage: string,
    attemptedUsername: string,
  ) => {
    if (errorMessage?.includes("has already been taken")) {
      return `${attemptedUsername} has already been taken`;
    }
    return errorMessage;
  };

  const onSubmit: SubmitHandler<FieldValues> = async (form) => {
    if (!form.username) return;

    if (!validateUsername(form.username)) {
      setState({
        ...state,
        error: true,
        errorMsg: "Invalid username format",
      });
      return;
    }

    setIsSubmitting(true);
    setLastAttemptedUsername(form.username);
    updateUsername({ variables: { input: { username: form.username } } });
  };

  const canChange = canChangeUsername();
  const timeRemaining = getTimeRemaining();

  return (
    <StyledSettingsContainer
      id="change-username"
      title="Change Username"
      mb={5}
    >
      <Divider />
      <Flex alignItems="center" pb={5}>
        <Box style={{ position: "relative" }}>
          {hasProAccess && !canChange ? (
            <UserIcon size={30} color="orange"></UserIcon>
          ) : (
            <AlertIcon size={30} color="envy"></AlertIcon>
          )}
        </Box>
        <Box pl={[5, 5, 3, 3]}>
          {hasProAccess && !canChange
            ? `Your username was recently changed. Next change available in ${formatTimeRemaining(
                timeRemaining,
              )}.`
            : "Username changes have a 1-month cooldown period, so choose wisely!"}
        </Box>
      </Flex>

      <form onSubmit={handleSubmit(onSubmit)}>
        <LabeledInputText
          input={{
            id: "username",
            defaultValue: currentUsername || "",
            disabled: !canChange,
            ...register("username", {
              required: canChange ? "Please enter a new username" : false,
              validate: (value) => {
                if (!canChange) return true;
                if (!value) return "Username is required";
                if (value === currentUsername)
                  return "Please enter a different username";
                return validateUsername(value)
                  ? true
                  : "Username must be 3-20 characters, alphanumeric with underscores/dashes only";
              },
            }),
          }}
          label="Username"
          error={errors.username}
          placeholder="Username"
        />

        {state.error && <Error>{state.errorMsg}</Error>}
        {state.success && <Success>{state.successMsg}</Success>}

        <Flex pt={[5]} justifyContent="flex-start">
          <Button
            size="small"
            flex={true}
            mt={3}
            disabled={!canChange || !formState.isDirty || isSubmitting}
            type="submit"
          >
            {isSubmitting
              ? "Changing..."
              : !hasProAccess
              ? showOverhaul
                ? "Upgrade To Standard"
                : "Upgrade To Change Username"
              : !canChange
              ? "Username Change On Cooldown"
              : "Change Username"}
          </Button>
        </Flex>
      </form>

      {!hasProAccess && (
        <Box mt={8}>
          <UpsellBox $isStandard={showOverhaul}>
            <UpsellTitle $isStandard={showOverhaul}>
              {showOverhaul
                ? "Unlock Username Changes with Standard"
                : "🚀 Unlock Username Changes with Pro"}
            </UpsellTitle>
            <UpsellText>
              {showOverhaul
                ? "Standard subscribers get the flexibility to change their username, plus access to advanced editing tools like Timeline Editor, montages, and more!"
                : "Pro subscribers get the flexibility to change their username, plus access to exclusive features like ad-free browsing, unlimited downloads and much more!"}
            </UpsellText>
            <UpgradeLink $isStandard={showOverhaul} href="/upgrade">
              {showOverhaul ? "View Standard Plan →" : "View Pro Plans →"}
            </UpgradeLink>
          </UpsellBox>
        </Box>
      )}
    </StyledSettingsContainer>
  );
};

const StyledSettingsContainer = styled(SettingsContainer)``;

const UpsellBox = styled(Box)<{ $isStandard?: boolean }>`
  border-radius: 8px;
  border: 2px solid
    ${(props) => (props.$isStandard ? "#00c8dc" : props.theme.colors.envy)};
  margin: 16px 0;
  padding: 20px;
`;

const UpsellTitle = styled(Text)<{ $isStandard?: boolean }>`
  color: ${(props) =>
    props.$isStandard ? "#00c8dc" : props.theme.colors.envy};
  font-size: 16px;
  font-weight: 600;
  margin-bottom: 8px;
`;

const UpsellText = styled(Text)`
  font-size: 14px;
  line-height: 1.4;
  margin-bottom: 12px;
`;

const UpgradeLink = styled(Link)<{ $isStandard?: boolean }>`
  align-items: center;
  color: ${(props) =>
    props.$isStandard ? "#00c8dc" : props.theme.colors.envy};
  display: inline-flex;
  font-weight: 500;
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
`;

export { UsernameChange };
